import { forwardRef } from "react";
import Button, { ButtonProps } from "../Button";
import { ToolbarItemAppearance, WithAriaLabelEnforcement } from "./types";
import { DataTrackingId } from "../../types";
import { useOverFlowItemContext } from "./internal/ToolbarItemOverflowContext";
import Tooltip from "../Tooltip";
import { ToolbarItemWrapper } from "./internal/ToolbarItemWrapper";
import { useTrackingId } from "../../hooks";
import { useToolbarSize } from "./internal/ToolbarContext";

import cx from "classnames";
import styles from "./Toolbar.module.scss";

/**
 * Base props for the ToolbarButton component
 * @extends Omit<ButtonProps, "appearance" | "aria-label" | "children"  "onClick">
 */
type ToolbarButtonBaseProps = Omit<
  ButtonProps,
  "appearance" | "aria-label" | "children" | "onClick"
> & {
  /**
   * Visual appearance of the button
   * @default ghost
   */
  appearance?: ToolbarItemAppearance;
  /**
   * Click event handler for the button
   */
  onClick: ButtonProps["onClick"];
};

/**
 * Props for the ToolbarButton component
 * @extends WithAriaLabelEnforcement<ToolbarButtonBaseProps>
 */
export type ToolbarButtonProps =
  WithAriaLabelEnforcement<ToolbarButtonBaseProps> & DataTrackingId;

/**
 * ToolbarButton component for standard clickable actions within a toolbar.
 *
 * Features:
 * - Designed specifically for toolbar integration
 * - Consistent styling with other toolbar items
 * - Automatic tooltip for icon-only buttons
 * - Overflow menu support
 * - Accessibility enforcement for aria-labels
 * - Ghost and primary appearance options
 *
 * @example
 * <ToolbarButton icon={BoldIcon} aria-label="Bold text" />
 *
 * @example
 * <ToolbarButton appearance="primary">
 *   Save
 * </ToolbarButton>
 */
export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  (props, ref) => {
    const { appearance = "ghost", className, children, ...rest } = props;

    // Checks if this component is rendered inside the overflow menu and fallbacks when the context is unavailable
    const { isInOverflowMenu = false } = useOverFlowItemContext?.() || {};
    const size = useToolbarSize();

    const trackingId = useTrackingId({
      name: "ToolbarButton",
      data: {
        disabled: props.disabled,
        "aria-label": props["aria-label"],
        children: props.children,
      },
      hasOverride: !!props["data-tracking-id"],
    });

    const buttonClassNames = cx(className, styles["toolbar-button-item"]);

    const item = children ? (
      <Button
        ref={ref}
        data-tracking-id={trackingId}
        appearance={appearance}
        size={size}
        data-anv="toolbar-button"
        className={buttonClassNames}
        {...rest}
      >
        {children}
      </Button>
    ) : (
      <Tooltip>
        <Tooltip.Trigger>
          <Button
            ref={ref}
            data-tracking-id={trackingId}
            appearance={appearance}
            size={size}
            data-anv="toolbar-button"
            className={buttonClassNames}
            {...rest}
          />
        </Tooltip.Trigger>
        <Tooltip.Content>{rest["aria-label"]}</Tooltip.Content>
      </Tooltip>
    );

    if (isInOverflowMenu) {
      return item;
    }

    return (
      <ToolbarItemWrapper item={{ itemProps: props, itemType: "button" }}>
        {item}
      </ToolbarItemWrapper>
    );
  },
);

ToolbarButton.displayName = "ToolbarButton";
